/* ─────────────────────────────────────────────
   PeopleSuiteSidebar.constants.ts
   Shared constants for the sidebar component
───────────────────────────────────────────── */

export interface NavItemDef {
  label: string;
  icon: string;
  path: string;
  multiLine?: boolean;
  roles?: string[];
  groupOwnerOnly?: boolean;
  /** Show if user has one of these roles OR is a group owner */
  groupOwnerOrRoles?: string[];
  /** Show for 'employee'-role users whose department (case-insensitive/trimmed)
   *  is in this list, even if their role isn't in `roles` */
  departments?: string[];
}

export interface NavSectionDef {
  header?: string;
  items: NavItemDef[];
}

/* ── Layout sizes (px) ──
   These are also written to CSS custom properties (see CSS_VAR below)
   so the .scss file can stay static while still reflecting these values. */
export const EXPANDED_WIDTH = 230;
export const COLLAPSED_WIDTH = 44;
export const HANDLE_WIDTH = 4;
export const RESIZE_MAX_WIDTH = 280;
/** How close (px) to COLLAPSED_WIDTH before a manual resize snaps into the collapsed state */
export const RESIZE_COLLAPSE_SNAP_THRESHOLD = 10;

/** CSS custom property names the .scss file reads its widths from */
export const CSS_VAR = {
  expandedWidth: '--ps-sidebar-expanded-width',
  collapsedWidth: '--ps-sidebar-collapsed-width',
  handleWidth: '--ps-sidebar-handle-width',
} as const;

import { ADMIN_ROLES } from '../../utils/roleHelpers';

/* ── Roles ── */
export const EMPLOYEE_ADMIN_ROLES = ['admin', 'employee'];

/* ── Nav item stagger animation timing (seconds) ── */
export const NAV_ITEM_ANIMATION_BASE_DELAY_S = 0.35;
export const NAV_ITEM_ANIMATION_STEP_S = 0.05;
export const NAV_ITEM_ANIMATION_DURATION_S = 0.4;

/* ── Tooltip offsets (px) ── */
export const TOOLTIP_VERTICAL_CENTER_OFFSET_PX = 14;
export const TOOLTIP_HORIZONTAL_GAP_PX = 10;

/* ── Toast timing (ms) ── */
export const TOAST_DISPLAY_DURATION_MS = 2500;
export const TOAST_EXIT_ANIMATION_MS = 500;

/* ── Toast icons / messages ── */
export const TOAST_ICON_DEFAULT = 'info';
export const TOAST_ICON_NAVIGATE = 'navigate_next';
export const TOAST_ICON_RESIZE = 'width';
export const TOAST_MSG_SIDEBAR_RESIZED = 'Sidebar resized';

/* ── Custom DOM events (shared contract with PeopleSuiteNavbar) ── */
export const EVENT_NAV_CLICK = 'ps:nav:click';
export const EVENT_SIDEBAR_TOGGLE = 'ps:sidebar:toggle';

/* ── DOM ids / selectors ── */
export const DOM_ID = {
  styleEl: 'ps-sidebar-styles',
  wrapper: 'ps-sidebar-wrapper',
  sidebar: 'ps-sidebar',
  resizeHandle: 'ps-resize-handle',
  tooltip: 'ps-sidebar-tooltip',
} as const;

export const SELECTOR = {
  mainContent: '.db-main',
  navItem: '.ps-nav-item',
  toastContainer: '.ps-toast-container',
  breadcrumbActive: '#ps-breadcrumb-active',
} as const;

/* ── Nav sections/items shown in the sidebar ── */
export const NAV_SECTIONS: NavSectionDef[] = [
  {
    items: [{ label: 'Dashboard', icon: 'dashboard', path: '/dashboard' }],
  },
  {
    header: 'COMPANY ADMINISTRATION',
    items: [
      { label: 'User Management', icon: 'person', path: '/users', roles: ADMIN_ROLES },
      { label: 'Group Management', icon: 'group', path: '/groups', roles: ADMIN_ROLES },
      { label: 'Manage Payslips', icon: 'payments', path: '/payroll/manage', roles: ADMIN_ROLES },
    ],
  },
  {
    header: 'EMPLOYEE LIFECYCLE',
    items: [],
  },
  {
    header: 'EMPLOYEE SERVICES',
    items: [{ label: 'Tickets', icon: 'confirmation_number', path: '/tickets' }],
  },
  {
    header: 'PAYROLL',
    items: [{ label: 'My Payslips', icon: 'receipt_long', path: '/payroll/my-payslips' }],
  },
  {
    header: 'TEAM MANAGEMENT',
    items: [
      {
        label: 'Employees',
        icon: 'people',
        path: '/management/employees',
        groupOwnerOrRoles: ADMIN_ROLES,
      },
    ],
  },
  {
    header: 'REPORTS & ANALYTICS',
    items: [],
  },
];
